import React, { useEffect, useRef, useState } from "react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import "./style.css";
import acompanhamentoApi from "../../service/modules/acompanhamento.api";
import freightService from "../../service/freight.service";
import { MINUTOS_SEM_GPS } from "../../util/freight/acompanhamento-marcacoes";
import { translatorForTheStatusOfStoppingPoints } from "../../view/frete-copy/action/action";
import {
  AZUL_DO_TRAJETO,
  CINZA_PERCURSO,
  COR_DA_COLETA,
  COR_DA_ENTREGA,
  COR_DA_LACUNA,
  corDoStatus,
} from "./cores";

/** A mesma base das outras telas de mapa, para o painel não parecer dois sistemas. */
const TILE_CLARO = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

/**
 * Teto de páginas do trajeto. Cinco páginas de mil capturas cobrem uma viagem de 17 dias na cadência
 * de 5 min; acima disso a tela avisa que faltou trecho em vez de continuar puxando calado.
 */
const MAX_PAGINAS = 5;

/** Acima disso a nuvem de bolinhas cinza vira borrão e a polyline sozinha lê melhor. */
const TETO_DE_PONTOS_DESENHADOS = 400;

/** ~66 m. Duas paradas vizinhas dentro disso são a mesma parada partida na fronteira da página. */
const RAIO_DE_FUSAO = 6e-4;

function coordenadaValida(par) {
  return (
    Array.isArray(par) &&
    Number.isFinite(Number(par[0])) &&
    Number.isFinite(Number(par[1])) &&
    Number(par[0]) !== 0 &&
    Number(par[1]) !== 0
  );
}

function numero(valor) {
  const n = Number(valor);
  return Number.isFinite(n) && n !== 0 ? n : null;
}

function paraMillis(valor) {
  if (valor == null) return null;
  if (typeof valor === "number") return Number.isFinite(valor) ? valor : null;
  if (typeof valor.toMillis === "function") return valor.toMillis();
  if (typeof valor.seconds === "number") {
    return valor.seconds * 1000 + Math.floor((valor.nanoseconds || 0) / 1e6);
  }
  const d = new Date(valor).getTime();
  return Number.isNaN(d) ? null : d;
}

function duracao(minutos) {
  if (!minutos) return "menos de 1min";
  if (minutos < 60) return `${minutos}min`;
  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;
  return resto ? `${horas}h${String(resto).padStart(2, "0")}` : `${horas}h`;
}

function dataHora(ms) {
  return ms ? new Date(ms).toLocaleString("pt-BR") : "—";
}

function rotuloDoStatus(status) {
  const traduzido = translatorForTheStatusOfStoppingPoints(status);
  return Array.isArray(traduzido) ? String(status || "Etapa") : traduzido;
}

/**
 * O resumo vem por página, então uma parada que começa no fim de uma e termina no começo da outra
 * volta partida em duas. Só paradas vizinhas se fundem, e só dentro do raio.
 */
function fundirParadas(paradas) {
  const fundidas = [];
  for (const parada of paradas) {
    const anterior = fundidas[fundidas.length - 1];
    if (
      anterior &&
      Math.abs(anterior.latitude - parada.latitude) < RAIO_DE_FUSAO &&
      Math.abs(anterior.longitude - parada.longitude) < RAIO_DE_FUSAO
    ) {
      anterior.fimMs = parada.fimMs;
      anterior.minutos = Math.round(
        (anterior.fimMs - anterior.inicioMs) / 60000,
      );
      anterior.pontos += parada.pontos;
      continue;
    }
    fundidas.push({ ...parada });
  }
  return fundidas;
}

function pinoDeGota(cor, titulo) {
  return L.divIcon({
    className: "mapa-rota__pino",
    html: `<span title="${titulo}" style="background:${cor}"></span>`,
    iconSize: [22, 22],
    iconAnchor: [11, 22],
  });
}

/**
 * O motorista não é gota como coleta e entrega: é redondo, maior, escuro e leva o caminhão dentro.
 *
 * Os três eram o mesmo pino de 22 px mudando só a cor, e o do motorista era um vermelho vizinho do
 * vermelho da entrega — de longe, dois pinos iguais. Quem olha o mapa precisa achar o caminhão em
 * um relance, não comparar tons.
 */
function pinoDoMotorista(titulo) {
  return L.divIcon({
    className: "mapa-rota__motorista",
    html: `<span class="mapa-rota__motorista-pulso"></span>
      <span class="mapa-rota__motorista-corpo" title="${titulo}">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M2 6.5h11.2v8.2H2z" />
          <path d="M13.2 9.4h3.6l3.2 3.1v2.2h-6.8z" />
          <circle cx="6.6" cy="17" r="2.1" />
          <circle cx="16.9" cy="17" r="2.1" />
        </svg>
      </span>`,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
  });
}

function MapaRota({ frete, compacto = false }) {
  const containerRef = useRef(null);
  const mapaRef = useRef(null);
  const camadaRef = useRef(null);
  const enquadradoRef = useRef(null);
  const [resumo, setResumo] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);
  const [aoVivo, setAoVivo] = useState(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || mapaRef.current) return undefined;

    const mapa = L.map(el, {
      zoomControl: true,
      attributionControl: true,
    }).setView([-15.78, -47.93], 4);
    L.tileLayer(TILE_CLARO, {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(mapa);
    mapaRef.current = mapa;

    // O container tem altura elástica no painel de tempo real: ele acompanha a tabela ao lado, que
    // muda de altura quando o operador troca o tamanho da página. O Leaflet calcula o tamanho uma
    // vez na criação e não observa o DOM — sem isto o mapa fica com faixa cinza onde cresceu.
    const observador = new ResizeObserver(() => mapa.invalidateSize());
    observador.observe(el);

    return () => {
      observador.disconnect();
      mapa.remove();
      mapaRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!frete?.id) {
      setResumo(null);
      setErro(null);
      return undefined;
    }

    let cancelado = false;
    const juntas = { trechos: [], paradas: [], lacunas: [], operacionais: [] };

    (async () => {
      setCarregando(true);
      setErro(null);

      let cursor;
      let paginas = 0;
      let ultimoMs = null;
      let ultimaCoordenada = null;
      let meta = {};

      while (paginas < MAX_PAGINAS) {
        const resposta = await acompanhamentoApi.buscarTrajeto(
          frete.id,
          cursor ? { cursor } : undefined,
        );
        if (cancelado) return;

        const pagina = resposta?.data?.data || {};
        meta = pagina.resumo || {};

        // Buraco de rastreio bem na virada da página: o resumo é por página e nenhuma das duas
        // enxerga a vizinha, então é aqui que ele aparece.
        const trechosDaPagina = (pagina.trechos || []).map((trecho) => [
          ...trecho,
        ]);
        const primeiraDaPagina = trechosDaPagina[0]?.[0];
        const houveBuracoNaVirada =
          Boolean(ultimoMs) &&
          Boolean(meta.primeiroMs) &&
          Boolean(ultimaCoordenada) &&
          Boolean(primeiraDaPagina) &&
          (meta.primeiroMs - ultimoMs) / 60000 > MINUTOS_SEM_GPS;

        if (houveBuracoNaVirada) {
          juntas.lacunas.push({
            deMs: ultimoMs,
            ateMs: meta.primeiroMs,
            minutos: Math.round((meta.primeiroMs - ultimoMs) / 60000),
            de: ultimaCoordenada,
            ate: primeiraDaPagina,
          });
        }

        // Sem buraco na virada, o primeiro trecho da página seguinte é a continuação do último: são
        // duas metades da mesma linha, cortadas só porque o cursor caiu ali.
        const anterior = juntas.trechos[juntas.trechos.length - 1];
        if (anterior && !houveBuracoNaVirada && trechosDaPagina.length) {
          anterior.push(...trechosDaPagina.shift());
        }
        juntas.trechos.push(...trechosDaPagina);

        juntas.paradas.push(...(pagina.paradas || []));
        juntas.lacunas.push(...(pagina.lacunas || []));
        juntas.operacionais.push(...(pagina.operacionais || []));

        ultimoMs = meta.ultimoMs ?? ultimoMs;
        const ultimoTrecho = juntas.trechos[juntas.trechos.length - 1];
        ultimaCoordenada =
          ultimoTrecho?.[ultimoTrecho.length - 1] || ultimaCoordenada;
        paginas += 1;
        cursor = meta.proximoCursor;

        // Desenha o que já chegou em vez de segurar a tela até a viagem inteira descer.
        setResumo({
          ...juntas,
          trechos: juntas.trechos.map((trecho) => [...trecho]),
          paradas: fundirParadas(juntas.paradas),
          formato: meta.formato,
          legendaDisponivel: meta.legendaDisponivel,
          faltouTrecho: Boolean(cursor) && paginas >= MAX_PAGINAS,
        });

        if (!cursor) break;
      }
    })()
      .catch((e) => {
        if (cancelado) return;
        setErro("Não foi possível carregar o trajeto deste frete.");
        console.error("Erro ao carregar o trajeto do frete:", e);
      })
      .finally(() => {
        if (!cancelado) setCarregando(false);
      });

    return () => {
      cancelado = true;
    };
  }, [frete?.id]);

  useEffect(() => {
    if (!frete?.id) {
      setAoVivo(null);
      return undefined;
    }

    setAoVivo(null);
    const cancelar = freightService.subscribeUltimaPosicao(
      frete.id,
      (snap) => {
        const doc = snap?.docs?.[0];
        setAoVivo(doc ? doc.data() : null);
      },
      (e) => console.error("Erro ao acompanhar a posição do motorista:", e),
    );

    return () => cancelar && cancelar();
  }, [frete?.id]);

  useEffect(() => {
    const mapa = mapaRef.current;
    if (!mapa) return;

    if (camadaRef.current) {
      mapa.removeLayer(camadaRef.current);
      camadaRef.current = null;
    }
    if (!frete) return;

    const camada = L.layerGroup().addTo(mapa);
    camadaRef.current = camada;
    const enquadrar = [];

    // Uma polyline por trecho contínuo. Ligar tudo numa linha só desenharia por cima do buraco de
    // rastreio um caminho que ninguém registrou.
    const linha = [];
    for (const trecho of resumo?.trechos || []) {
      const pontosDoTrecho = trecho.filter(coordenadaValida);
      if (!pontosDoTrecho.length) continue;
      if (pontosDoTrecho.length > 1) {
        L.polyline(pontosDoTrecho, {
          color: AZUL_DO_TRAJETO,
          weight: 4,
          opacity: 0.75,
          smoothFactor: 2,
        }).addTo(camada);
      }
      linha.push(...pontosDoTrecho);
    }
    enquadrar.push(...linha);

    if (linha.length && linha.length <= TETO_DE_PONTOS_DESENHADOS) {
      for (const par of linha) {
        L.circleMarker(par, {
          radius: 2.5,
          color: CINZA_PERCURSO,
          weight: 0,
          fillColor: CINZA_PERCURSO,
          fillOpacity: 0.85,
        }).addTo(camada);
      }
    }

    for (const lacuna of resumo?.lacunas || []) {
      if (!coordenadaValida(lacuna.de) || !coordenadaValida(lacuna.ate))
        continue;
      L.polyline([lacuna.de, lacuna.ate], {
        color: COR_DA_LACUNA,
        weight: 3,
        opacity: 0.9,
        dashArray: "6 8",
      })
        .bindTooltip(
          `Sem rastreio por ${duracao(lacuna.minutos)} — de ${dataHora(lacuna.deMs)} a ${dataHora(lacuna.ateMs)}`,
        )
        .addTo(camada);
    }

    for (const parada of resumo?.paradas || []) {
      const par = [parada.latitude, parada.longitude];
      if (!coordenadaValida(par)) continue;
      L.circleMarker(par, {
        radius: compacto ? 6 : 8,
        color: "#ffffff",
        weight: 2,
        fillColor: CINZA_PERCURSO,
        fillOpacity: 1,
      })
        .bindTooltip(
          `Parado ${duracao(parada.minutos)} · ${parada.pontos} capturas · desde ${dataHora(parada.inicioMs)}`,
        )
        .addTo(camada);
    }

    for (const etapa of resumo?.operacionais || []) {
      const par = [etapa.latitude, etapa.longitude];
      if (!coordenadaValida(par)) continue;
      L.circleMarker(par, {
        radius: compacto ? 5 : 7,
        color: "#ffffff",
        weight: 2,
        fillColor: corDoStatus(etapa.freightStatus),
        fillOpacity: 1,
      })
        .bindTooltip(
          `${rotuloDoStatus(etapa.freightStatus)} — ${dataHora(etapa.ms)}`,
        )
        .addTo(camada);
    }

    const marcarPino = (lat, lng, cor, titulo) => {
      const y = numero(lat);
      const x = numero(lng);
      if (y === null || x === null) return;
      L.marker([y, x], { icon: pinoDeGota(cor, titulo) })
        .bindTooltip(titulo)
        .addTo(camada);
      enquadrar.push([y, x]);
    };

    marcarPino(
      frete?.firstDelivery?.coordinates?.latitude,
      frete?.firstDelivery?.coordinates?.longitude,
      COR_DA_COLETA,
      "Coleta",
    );
    marcarPino(
      frete?.lastDelivery?.coordinates?.latitude,
      frete?.lastDelivery?.coordinates?.longitude,
      COR_DA_ENTREGA,
      "Entrega",
    );

    const motorista = aoVivo || frete?.lastPosition;
    const quando = paraMillis(motorista?.timestamp ?? motorista?.capturedAt);
    const lat = numero(motorista?.latitude);
    const lng = numero(motorista?.longitude);
    if (lat !== null && lng !== null) {
      const titulo = `Motorista${quando ? ` — ${dataHora(quando)}` : ""}`;
      // por último e com zIndexOffset: o caminhão passa por cima de parada e etapa quando o
      // motorista está em cima delas, que é o caso mais comum — parado na doca.
      L.marker([lat, lng], {
        icon: pinoDoMotorista(titulo),
        zIndexOffset: 1000,
      })
        .bindTooltip(titulo)
        .addTo(camada);
      enquadrar.push([lat, lng]);
    }

    // Só enquadra ao trocar de frete. Reenquadrar a cada posição nova arrancaria o mapa da mão de
    // quem estivesse arrastando.
    if (enquadrar.length && enquadradoRef.current !== frete.id) {
      enquadradoRef.current = frete.id;
      if (enquadrar.length === 1) mapa.setView(enquadrar[0], 13);
      else mapa.fitBounds(enquadrar, { padding: [28, 28] });
    }
  }, [frete, resumo, aoVivo, compacto]);

  const pontos = (resumo?.trechos || []).reduce(
    (soma, trecho) => soma + trecho.length,
    0,
  );

  return (
    <div className={compacto ? "mapa-rota mapa-rota--compacto" : "mapa-rota"}>
      <div ref={containerRef} className="mapa-rota__tela" />

      {!compacto && (
        <div className="mapa-rota__legenda">
          <span>
            <i style={{ background: COR_DA_COLETA }} /> Coleta
          </span>
          <span>
            <i style={{ background: COR_DA_ENTREGA }} /> Entrega
          </span>
          <span>
            <i className="mapa-rota__legenda-caminhao">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M2 6.5h11.2v8.2H2z" />
                <path d="M13.2 9.4h3.6l3.2 3.1v2.2h-6.8z" />
                <circle cx="6.6" cy="17" r="2.1" />
                <circle cx="16.9" cy="17" r="2.1" />
              </svg>
            </i>{" "}
            Motorista
          </span>
          <span>
            <i style={{ background: AZUL_DO_TRAJETO }} /> Trajeto percorrido
          </span>
          <span>
            <i style={{ background: CINZA_PERCURSO }} /> Parada e deslocamento
          </span>
          <span>
            <i className="mapa-rota__tracejado" /> Sem rastreio
          </span>
          <span>
            <i style={{ background: corDoStatus("WAITING_LOADING") }} />{" "}
            Aguardando
          </span>
          <span>
            <i style={{ background: corDoStatus("IN_TRANSIT") }} /> Em trânsito
          </span>
          <span>
            <i style={{ background: corDoStatus("UNLOADING_COMPLETED") }} />{" "}
            Etapa concluída
          </span>
        </div>
      )}

      <p className="mapa-rota__nota">
        {!frete ? (
          "Selecione um frete na lista."
        ) : erro ? (
          <span className="mapa-rota__nota--erro">{erro}</span>
        ) : carregando && !pontos ? (
          "Carregando o trajeto..."
        ) : !pontos ? (
          "Este frete ainda não tem posição registrada."
        ) : (
          <>
            {pontos} {pontos === 1 ? "ponto" : "pontos"} no trajeto
            {resumo?.paradas?.length
              ? ` · ${resumo.paradas.length} parada(s)`
              : ""}
            {resumo?.lacunas?.length
              ? ` · ${resumo.lacunas.length} intervalo(s) sem rastreio`
              : ""}
            {resumo?.legendaDisponivel === false
              ? " · rastreio no formato antigo, sem etapa operacional para colorir"
              : ""}
            {resumo?.faltouTrecho ? " · trecho mais antigo não carregado" : ""}
          </>
        )}
      </p>
    </div>
  );
}

export default MapaRota;
